import type { QueryClient } from "@tanstack/svelte-query";
import { calculateCreditLine, type Image } from "bildebanken-model";
import {
  mapBildebankenImageToSelectedImage,
  type PublishedImage,
} from "components/Plugin/imageTypes";
import { appendKaleidoDerivateUrlToImage } from "components/Plugin/services/derivate";
import { publishAndSelect } from "components/Plugin/publishAndSelect";
import { closePluginCallback, updateCallback } from "services/pluginMessages";
import {
  getEditOnlyMode,
  getRequiredFields,
  getSelectImageOnSave,
} from "services/plugin-customization";
import { pageState } from "state/page";
import { uploads } from "state/upload";
import { get, writable } from "svelte/store";
import { interpret } from "xstate";
import {
  editImageMachine,
  type EditableMetadataFieldKey,
  type EditImageActor,
  type EditImageState,
} from "./editImageMachine";
import { isPlugin } from "state/app";

export const editActor = writable<EditImageActor | undefined>(undefined);
export const editState = writable<EditImageState | undefined>(undefined);
export const selectImageOnSave = writable(false);

// Held separately from the store since ActorRef does not expose stop()
let stopCurrentActor: (() => void) | undefined;

export type InitEditSessionParams = {
  itemIds: string[];
  queryClient: QueryClient;
  onDone: () => void;
};

function mapStateToPublishedImage(imageItem: Image): PublishedImage {
  return <Image & { metadata: { publicId: string } }>{
    ...imageItem,
    metadata: {
      ...imageItem.metadata,
      creditLine: calculateCreditLine(imageItem.metadata),
      publicId: imageItem.metadata.publicId || "",
    },
  };
}

function handleDone(
  state: EditImageState,
  editOnlyMode: boolean,
  shouldSelectImageOnSave: boolean,
  onDone: () => void,
): void {
  onDone();
  if (!isPlugin) return;

  if (editOnlyMode) {
    const selectedImage = mapBildebankenImageToSelectedImage(
      mapStateToPublishedImage(state.context.images[0]),
    );
    appendKaleidoDerivateUrlToImage(selectedImage).then(() => {
      updateCallback(selectedImage);
      closePluginCallback();
    });
  } else if (shouldSelectImageOnSave) {
    publishAndSelect(state.context.images[0]);
  }
}

export function initEditSession({ itemIds, queryClient, onDone }: InitEditSessionParams): void {
  const currentPageState = get(pageState);
  const shouldSelectImageOnSave =
    (getSelectImageOnSave() && itemIds.length === 1 && currentPageState.type === "UPLOAD") ||
    (currentPageState.type === "EDIT" &&
      currentPageState.selectImageAfterEdit &&
      itemIds.length === 1);

  const userDefinedRequiredFields = getRequiredFields();
  let requiredFields: EditableMetadataFieldKey[] = ["title", "rights"];
  if (userDefinedRequiredFields && shouldSelectImageOnSave) {
    requiredFields = [
      ...requiredFields,
      ...(userDefinedRequiredFields as EditableMetadataFieldKey[]),
    ];
  }

  const editOnlyMode = getEditOnlyMode() ?? false;

  selectImageOnSave.set(shouldSelectImageOnSave ?? false);

  const actor = interpret(
    editImageMachine.withContext({
      images: [],
      originals: [],
      changes: [],
      failedUploadImageIds: [],
      current: 0,
      selectedIds: itemIds,
      imageIds: itemIds,
      requiredFields,
      uploads,
      client: queryClient,
      imageIdToIndexMap: new Map(),
    }),
  );

  let done = false;
  actor.subscribe((state) => {
    editState.set(state);
    if (state.matches("Done") && !done) {
      done = true;
      handleDone(state, editOnlyMode, shouldSelectImageOnSave ?? false, onDone);
    }
  });

  actor.start();
  stopCurrentActor = () => actor.stop();
  editActor.set(actor);
}

export function teardownEditSession(): void {
  stopCurrentActor?.();
  stopCurrentActor = undefined;
  editActor.set(undefined);
  editState.set(undefined);
  selectImageOnSave.set(false);
}
